import React, { useEffect, useState, useRef } from 'react';
import { Outlet, useParams, useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import LearningSidebar from '../../components/LearningSidebar';
import { useTheme } from '../../context/ThemeContext';
import {
  useChampionTheme,
  ChampionProgress, CrownIcon, TrophyIcon, GlowText, ChampionBadge,
} from '../../components/ChampionElements';

function BannerCarousel({ banners, t }) {
  const carouselRef = useRef(null);
  const instanceRef = useRef(null);

  useEffect(() => {
    if (!banners || banners.length < 2) return;
    // Bootstrap 5 carousel auto-init via data-bs-ride
    const el = carouselRef.current;
    if (!el) return;
    // Manually init for better control
    if (window.bootstrap) {
      instanceRef.current = new window.bootstrap.Carousel(el, {
        interval: 5000,
        ride: 'carousel',
        pause: 'hover',
        wrap: true,
      });
    }
    return () => {
      if (instanceRef.current) {
        instanceRef.current.dispose();
      }
    };
  }, [banners]);

  if (!banners || banners.length === 0) return null;

  if (banners.length === 1) {
    const b = banners[0];
    return (
      <div className="d-flex justify-content-center" style={{ maxWidth: '700px', margin: '0 auto' }}>
        {banners[0].link_url ? (
          <a href={banners[0].link_url} target="_blank" rel="noreferrer" style={{ width: '100%' }}>
            <img src={`data:image/jpeg;base64,${b.image}`} alt={b.title}
              style={{ width: '100%', maxHeight: '160px', objectFit: 'cover', borderRadius: '12px', border: `1px solid ${t.gold}22` }} />
          </a>
        ) : (
          <img src={`data:image/jpeg;base64,${b.image}`} alt={b.title}
            style={{ width: '100%', maxHeight: '160px', objectFit: 'cover', borderRadius: '12px', border: `1px solid ${t.gold}22` }} />
        )}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto' }}>
      <div id="bannerCarousel" className="carousel slide" ref={carouselRef} data-bs-ride="carousel" data-bs-interval="5000">
        {/* Indicators */}
        <div className="carousel-indicators">
          {banners.map((_, i) => (
            <button key={i} type="button" data-bs-target="#bannerCarousel" data-bs-slide-to={i}
              className={i === 0 ? 'active' : ''} aria-current={i === 0 ? 'true' : undefined}
              aria-label={`Slide ${i + 1}`} />
          ))}
        </div>

        {/* Slides */}
        <div className="carousel-inner" style={{ borderRadius: '12px', border: `1px solid ${t.gold}22` }}>
          {banners.map((b, i) => (
            <div key={b.id} className={`carousel-item ${i === 0 ? 'active' : ''}`}>
              {b.link_url ? (
                <a href={b.link_url} target="_blank" rel="noreferrer">
                  <img src={`data:image/jpeg;base64,${b.image}`} className="d-block w-100"
                    alt={b.title} style={{ maxHeight: '160px', objectFit: 'cover', borderRadius: '12px' }} />
                </a>
              ) : (
                <img src={`data:image/jpeg;base64,${b.image}`} className="d-block w-100"
                  alt={b.title} style={{ maxHeight: '160px', objectFit: 'cover', borderRadius: '12px' }} />
              )}
              {/* Title overlay */}
              <div className="carousel-caption d-none d-md-block" style={{
                background: 'linear-gradient(0deg, rgba(0,0,0,0.6), transparent)',
                borderRadius: '0 0 12px 12px', bottom: 0, left: 0, right: 0, padding: '12px 20px',
              }}>
                <p className="mb-0 fw-semibold small">{b.title}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Controls */}
        <button className="carousel-control-prev" type="button" data-bs-target="#bannerCarousel" data-bs-slide="prev">
          <span className="carousel-control-prev-icon" aria-hidden="true" style={{ filter: 'drop-shadow(0 0 4px rgba(0,0,0,0.5))' }} />
          <span className="visually-hidden">Previous</span>
        </button>
        <button className="carousel-control-next" type="button" data-bs-target="#bannerCarousel" data-bs-slide="next">
          <span className="carousel-control-next-icon" aria-hidden="true" style={{ filter: 'drop-shadow(0 0 4px rgba(0,0,0,0.5))' }} />
          <span className="visually-hidden">Next</span>
        </button>
      </div>
    </div>
  );
}

export default function LearningLayout() {
  const { webinarId } = useParams();
  const navigate = useNavigate();
  const [webinar, setWebinar] = useState(null);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isDark, toggle } = useTheme();
  const t = useChampionTheme();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [webinarRes, bannersRes] = await Promise.all([
          api.get(`/api/member/webinars/${webinarId}`),
          api.get('/api/banners/active').catch(() => ({ data: [] })),
        ]);
        setWebinar(webinarRes.data);
        setBanners(bannersRes.data || []);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [webinarId, navigate]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: t.bg, display: 'flex', justifyContent: 'center', alignItems: 'center', transition: 'background-color 0.4s ease' }}>
        <div className="text-center">
          <TrophyIcon size={64} />
          <p className="mt-3" style={{ color: t.gold }}>Memuat kelasmu...</p>
          <div className="spinner-border" style={{ color: t.gold }} role="status" />
        </div>
      </div>
    );
  }

  if (!webinar) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: t.bg }}>
        <div className="container mt-5">
          <div className="alert" style={{ backgroundColor: t.errorBg, color: t.error, border: `1px solid ${t.error}22`, borderRadius: '16px' }}>
            Kelas tidak ditemukan.
          </div>
        </div>
      </div>
    );
  }

  const hasEpisodes = webinar.episodes && webinar.episodes.length > 0;
  const hasMaterials = webinar.materials && webinar.materials.length > 0;
  const hasTranscripts = webinar.transcripts && webinar.transcripts.length > 0;
  const hasQuiz = webinar.quizzes && webinar.quizzes.length > 0;
  const hasCertificate = webinar.certificate_config || webinar.has_certificate;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: t.bg, transition: 'background-color 0.4s ease' }}>
      {/* ── Champion Header ── */}
      <div
        style={{
          background: t.bgHeader,
          borderBottom: `2px solid ${t.headerBorder}`,
          padding: '20px 0',
          position: 'relative',
          overflow: 'hidden',
          transition: 'background 0.4s ease',
        }}
      >
        {/* Background decorative circles */}
        <div style={{ position: 'absolute', top: '-40px', right: '10%', width: '120px', height: '120px', borderRadius: '50%', background: t.decorativeCircle(0.15), pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-30px', left: '5%', width: '80px', height: '80px', borderRadius: '50%', background: t.decorativeCircle(0.1), pointerEvents: 'none' }} />

        {/* Theme toggle - top right */}
        <div style={{ position: 'absolute', top: '8px', right: '20px', zIndex: 5 }}>
          <button
            onClick={toggle}
            style={{
              background: 'transparent',
              border: `1.5px solid ${t.gold}44`,
              borderRadius: '50%',
              width: '40px', height: '40px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              color: t.gold,
              fontSize: '1.2rem',
              transition: 'all 0.3s ease',
            }}
            title={isDark ? 'Mode Terang' : 'Mode Gelap'}
          >
            <i className={`bi ${isDark ? 'bi-sun-fill' : 'bi-moon-fill'}`}></i>
          </button>
        </div>

        <div className="container-fluid">
          {/* Title row */}
          <div className="d-flex align-items-center gap-3 mb-2" style={{ paddingRight: '50px' }}>
            <CrownIcon size={28} />
            <GlowText as="h4" style={{ margin: 0, fontSize: 'clamp(1rem, 2.5vw, 1.5rem)' }}>
              {webinar.title}
            </GlowText>
          </div>

          {/* ── Banner Carousel ── */}
          <div className="mb-3" style={{ paddingRight: '50px' }}>
            <BannerCarousel banners={banners} t={t} />
          </div>

          {/* Coach + stats row */}
          <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
            {webinar.pemateri_name && (
              <ChampionBadge>
                <i className="bi bi-person-badge me-1"></i>
                Coach: {webinar.pemateri_name}
              </ChampionBadge>
            )}
            {hasEpisodes && (
              <ChampionBadge>
                <i className="bi bi-play-circle me-1"></i>
                {webinar.completed_episodes || 0}/{webinar.total_episodes || webinar.episodes?.length || 0} Episode
              </ChampionBadge>
            )}
            {hasCertificate && (
              <ChampionBadge color="#22C55E">
                <i className="bi bi-award me-1"></i>
                Sertifikat
              </ChampionBadge>
            )}
          </div>

          {/* Progress bar */}
          {hasEpisodes && (
            <div style={{ maxWidth: '500px' }}>
              <ChampionProgress
                value={webinar.progress || 0}
                label="Progress Pembelajaran"
                height={10}
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Main Content Area ── */}
      <div className="container-fluid mt-4">
        <div className="row">
          {/* Sidebar */}
          <div className="col-md-3 mb-4">
            <LearningSidebar
              webinarId={webinarId}
              hasEpisodes={hasEpisodes}
              hasMaterials={hasMaterials}
              hasTranscripts={hasTranscripts}
              hasQuiz={hasQuiz}
              hasCertificate={hasCertificate || webinar.certificate_config}
            />
          </div>

          {/* Main content */}
          <div className="col-md-9">
            <div
              style={{
                background: t.bgCard,
                borderRadius: '16px',
                padding: '24px',
                border: `1px solid ${t.borderLight}`,
                color: t.text,
                minHeight: '500px',
                transition: 'all 0.4s ease',
              }}
            >
              <Outlet context={{ webinar, webinarId }} />
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: '40px' }} />
    </div>
  );
}
